import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sun, Moon, Monitor, Menu, UserCircle, LogOut, User, LifeBuoy } from 'lucide-react';
import Sidebar from './Sidebar';
import MobileDock from './MobileDock';
import AppBanner from './AppBanner';
import BrandPresetSwitcher from './BrandPresetSwitcher';
import BrandFooter from './BrandFooter';
import NotificationBell from './NotificationBell';
import { api, setActiveTenantId as apiSetActiveTenantId, getActiveTenantId } from '../../lib/api';
import { useSession } from '../../lib/session';
import { useTheme, type ThemeMode } from '../../lib/useTheme';
import { usePublish } from '../../lib/usePublish';
import type { TenantSummary } from '../../lib/types';
import { resolveTenantSwitchDestination, TENANT_SWITCH_EVENT, type TenantSwitchEventDetail } from '../../lib/tenantSwitch';
import { HelpProvider, useHelp } from '../../lib/useHelp';
import HelpDrawer from '../help/HelpDrawer';

const THEME_CYCLE: ThemeMode[] = ['system', 'light', 'dark'];
const THEME_ICON = { system: Monitor, light: Sun, dark: Moon };
const THEME_LABEL = { system: 'Sistema', light: 'Claro', dark: 'Oscuro' };

// La ayuda (GOL-11) se abre desde tres sitios del shell —el botón «?», el menú de
// cuenta y el pie del Sidebar—, así que su estado vive en un provider que envuelve
// todo el layout en vez de bajarse por props hasta cada uno.
export default function Layout() {
  return (
    <HelpProvider>
      <LayoutShell />
    </HelpProvider>
  );
}

function LayoutShell() {
  const navigate = useNavigate();
  const { openHelp } = useHelp();
  const location = useLocation();
  const [tenantList, setTenantList] = useState<TenantSummary[]>([]);
  const [activeTenantId, setActiveTenantIdState] = useState<string | null>(getActiveTenantId());
  const [activeTenantName, setActiveTenantName] = useState<string | null>(null);
  const { user } = useSession();
  // Un saas_admin sin `pal_tenant` en localStorage (primera sesión) no tiene tenant
  // resuelto todavía: montar las rutas hijas antes de que el switcher hidrate dispararía
  // sus cargas de datos contra el fallback del Worker (UX-30). El resto de roles siempre
  // llega con `user.tenantId` fijo, así que arrancan resueltos.
  const [tenantResolved, setTenantResolved] = useState(
    user?.role !== 'saas_admin' || Boolean(getActiveTenantId()),
  );
  const { pending: publishPending } = usePublish();
  const { mode, setTheme } = useTheme();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
    }
    if (userMenuOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [userMenuOpen]);

  function cycleTheme() {
    const next = THEME_CYCLE[(THEME_CYCLE.indexOf(mode) + 1) % THEME_CYCLE.length];
    setTheme(next);
  }

  useEffect(() => {
    if (!user) return;

    if (user.role === 'saas_admin') {
      api.tenants.list().then((list) => {
        setTenantList(list);
        const current = getActiveTenantId();
        // No solo "sin tenant activo": un `pal_tenant` que apunta a un tenant ya
        // borrado (p. ej. el tenant de pruebas activo al momento de eliminarlo,
        // `InfrastructurePage.deleteActiveTestTenant`) es indistinguible de uno
        // válido para este chequeo si no se compara contra la lista fresca — y sin
        // corregirlo aquí, el sidebar y el header siguen sirviendo el nombre y los
        // datos del tenant borrado hasta que alguien cambia de tenant a mano.
        if (!current || !list.some((t) => t.id === current)) {
          const fallbackTenantId = user.tenantId ?? list[0]?.id ?? null;
          if (fallbackTenantId) {
            apiSetActiveTenantId(fallbackTenantId);
            setActiveTenantIdState(fallbackTenantId);
          }
        }
        // Recién aquí hay un tenant activo garantizado (propio, cacheado o el
        // fallback recién fijado): a partir de este punto es seguro dejar que las
        // rutas hijas empiecen a pedir datos (UX-30).
        setTenantResolved(true);
      }).catch(() => setTenantResolved(true));
      return;
    }

    void Promise.resolve().then(() => {
      setTenantList([]);
      setTenantResolved(true);
    });
  }, [user]);

  // La info del sitio activo (nombre en el header) también espera al tenant
  // resuelto: antes de eso no hay forma de saber de qué negocio es.
  useEffect(() => {
    if (!user || !tenantResolved) return;
    api.settings.getSite().then((site) => {
      setActiveTenantName(site?.name ?? null);
    }).catch(() => {
      setActiveTenantName(null);
    });
  }, [user, tenantResolved]);

  function handleTenantSwitch(id: string) {
    if (id === activeTenantId) return;
    const destination = resolveTenantSwitchDestination(location);
    const proceed = () => {
      apiSetActiveTenantId(id);
      setActiveTenantIdState(id);
      if (destination.replace) {
        window.location.replace(destination.href);
      } else {
        // El tenant vive fuera de la URL por diseño. Recargar la misma entrada
        // conserva historial, ruta, query y tab, y fuerza a cada vista a leer el
        // nuevo scope antes de renderizar datos.
        window.location.reload();
      }
    };
    const event = new CustomEvent<TenantSwitchEventDetail>(TENANT_SWITCH_EVENT, {
      cancelable: true,
      detail: { proceed },
    });
    if (window.dispatchEvent(event)) proceed();
  }

  async function handleLogout() {
    apiSetActiveTenantId(null);
    await api.auth.logout().catch(() => null);
    navigate(0);
  }

  return (
    <div className="flex h-dvh overflow-hidden bg-ui-bg">
      <Sidebar
        mobileOpen={mobileNavOpen}
        onMobileClose={() => setMobileNavOpen(false)}
        isSaasAdmin={user?.role === 'saas_admin'}
        role={user?.role ?? 'editor'}
        tenants={tenantList}
        activeTenantId={activeTenantId}
        activeTenantName={activeTenantName}
        onTenantSwitch={handleTenantSwitch}
      />

      <div className="relative z-0 flex flex-col flex-1 overflow-hidden min-w-0">
        <header className="relative z-header h-14 shrink-0 flex items-center justify-between gap-3 px-4 sm:px-6 border-b border-ui-border bg-ui-surface/60 backdrop-blur-sm">
          <button
            onClick={() => setMobileNavOpen(true)}
            className="md:hidden w-8 h-8 flex items-center justify-center rounded-md text-ui-text-subtle hover:text-ui-text hover:bg-ui-text/[0.06] transition-colors"
            aria-label="Abrir menú"
          >
            <Menu size={18} />
          </button>

          <div className="flex items-center gap-3 ml-auto">
            {publishPending && (
              <span
                className="flex items-center gap-1.5 rounded-full bg-ui-warning/10 px-2.5 py-1 text-xs font-medium text-ui-warning"
                title="Tu sitio se está publicando"
              >
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-ui-warning opacity-75" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-ui-warning" />
                </span>
                Publicando…
              </span>
            )}
            <BrandPresetSwitcher />
            <button
              onClick={() => openHelp()}
              title="Ayuda"
              aria-label="Abrir ayuda"
              className="w-8 h-8 flex items-center justify-center rounded-md text-ui-text-subtle hover:text-ui-text hover:bg-ui-text/[0.06] transition-colors"
            >
              <LifeBuoy size={16} />
            </button>
            <NotificationBell />
            <button
              onClick={cycleTheme}
              title={`Tema: ${THEME_LABEL[mode]}`}
              className="relative w-8 h-8 flex items-center justify-center rounded-md text-ui-text-subtle hover:text-ui-text hover:bg-ui-text/[0.06] transition-colors overflow-hidden"
            >
              <AnimatePresence mode="wait" initial={false}>
                <motion.span
                  key={mode}
                  initial={{ opacity: 0, scale: 0.6, rotate: -30 }}
                  animate={{ opacity: 1, scale: 1, rotate: 0 }}
                  exit={{ opacity: 0, scale: 0.6, rotate: 30 }}
                  transition={{ duration: 0.18 }}
                  className="absolute"
                >
                  {(() => { const Icon = THEME_ICON[mode]; return <Icon size={15} />; })()}
                </motion.span>
              </AnimatePresence>
            </button>

            {user?.email && (
              <div className="relative" ref={userMenuRef}>
                <button
                  onClick={() => setUserMenuOpen(o => !o)}
                  className="w-8 h-8 flex items-center justify-center rounded-full bg-ui-accent/15 text-ui-accent hover:bg-ui-accent/25 transition-colors"
                  aria-label="Menú de usuario"
                >
                  <UserCircle size={18} />
                </button>

                <AnimatePresence>
                  {userMenuOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 6, scale: 0.97 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 6, scale: 0.97 }}
                      transition={{ duration: 0.13 }}
                      className="absolute right-0 top-10 w-56 bg-ui-surface border border-ui-border rounded-xl shadow-xl z-dropdown overflow-hidden"
                    >
                      <div className="px-4 py-3 border-b border-ui-border">
                        <p className="text-xs text-ui-text-subtle">Sesión activa</p>
                        <p className="text-sm text-ui-text font-medium truncate mt-0.5">{user.email}</p>
                      </div>
                      <div className="py-1">
                        <button
                          onClick={() => { setUserMenuOpen(false); navigate('/account'); }}
                          className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-ui-text-muted hover:text-ui-text hover:bg-ui-text/[0.06] transition-colors"
                        >
                          <User size={15} className="shrink-0" />
                          Mi perfil
                        </button>
                        <button
                          onClick={() => { setUserMenuOpen(false); openHelp(); }}
                          className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-ui-text-muted hover:text-ui-text hover:bg-ui-text/[0.06] transition-colors"
                        >
                          <LifeBuoy size={15} className="shrink-0" />
                          Ayuda
                        </button>
                        <button
                          onClick={() => { setUserMenuOpen(false); handleLogout(); }}
                          className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-ui-danger hover:text-ui-danger hover:bg-ui-danger/10 transition-colors"
                        >
                          <LogOut size={15} className="shrink-0" />
                          Cerrar sesión
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}
          </div>
        </header>

        <main className="flex-1 overflow-y-auto overflow-x-hidden p-4 sm:p-6 pb-24 md:pb-6 bg-ui-bg">
          <AppBanner isSaasAdmin={user?.role === 'saas_admin'} />
          {tenantResolved ? <Outlet /> : null}
          <BrandFooter />
        </main>

        <MobileDock onOpenMore={() => setMobileNavOpen(true)} />
      </div>

      <HelpDrawer />
    </div>
  );
}
